import React, { useState, useEffect } from "react";
import { Paper, Typography, Grid2, Divider } from "@mui/material";
import { useForm } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';
import dayjs from "dayjs";

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import ControlledTextField from "@components/inputs/controlledTextField";
import ControlledAutocomplete from "@components/inputs/controlledAutocomplete";
import ControlledDatePicker from "@components/inputs/controlledDatePicker";
import ControlledRichTextEditor from "@components/inputs/controlledRichTextEditor";

import { useSnackbar } from "@context/snackbarContext";

import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";

import { formatDate, formatDateTime } from "@utils/formatter";

const schema = z.object({
  // broadcastType: z.object({
  //   broadcastType: z.string().min(1, "Label is required"),
  //   broadcastTypeGuid: z.string().min(1, "Value is required"),
  // }, {invalid_type_error: 'Broadcast Type is required'}),
  title: z.string().min(1, { message: 'Title is required' }),
  // broadcastDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({invalid_type_error: 'Broadcast Date is required'}))
  //   .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }),

  message: z.string().min(1, { message: 'Content is required' }),

  /* view only */
  createdOn: z.string().optional(),
  createdBy: z.string().optional(),
});

export default function NewsletterForm() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const navigate = useNavigate()
  const { pathname } = useLocation();
  const { user } = useAuthentication()
  const pageAction = pathname.split("/")[3] || null; // get from pathname expected values ["create" || "view" || "edit"]
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const { handleSubmit, control, getValues, reset, watch } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      status: "Active",
      message: ``
    },
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  useEffect(()=>{
    if (((pageAction === "edit" || pageAction === "view") && !id) || (pageAction === "create" && id)) {
      onBack()
    }
    if (id) {
      fetchBroadcastMutation.mutate(id)
    }
  },[pageAction])

  const fetchBroadcastMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${AdminApiEndpoints.getBroadcast}/${id}`)
    },
    onSuccess: (res) => {
      const data = res.data
      data.broadcastType = {broadcastType: data.broadcastType?.valueEng, broadcastTypeGuid: data.broadcastType?.parameterGuid}
      data.createdOn = formatDateTime(data.createdOn)
      reset(data)
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve newsletter", "error");
    }
  });

  // const broadcastTypeOptions = useQuery({
  //   queryKey: ['getBroadcastTypeList'],
  //   queryFn: () =>
  //     axios.get(AdminApiEndpoints.getBroadcastTypeList),
  //   initialData: [],
  //   select: (res) => {
  //     const data = res?.data || [];
  //     return data.map(item => ({
  //         broadcastTypeGuid: item.parameterGuid,
  //         broadcastType: item.valueEng
  //     }));
  //   },
  // })

  // console.log(broadcastTypeOptions.data)

  // const broadcastStatusOptions = useQuery({
  //   queryKey: ['getBroadcastStatusList'],
  //   queryFn: () =>
  //     axios.get(AdminApiEndpoints.getBroadcastStatusList),
  //   initialData: [],
  //   select: (res) => res?.data || [],
  // })
  // console.log(broadcastStatusOptions.data)

  const createBroadcastMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(AdminApiEndpoints.createBroadcast, {...data, by: user?.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getBroadcast'] })
      openSnackbar("Successfully created broadcast", "success");
      onBack()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to create broadcast", "error");
    }
  })

  const updateBroadcastkMutation = useMutation({
    mutationFn: (data) => {
      // return axios.put(`${AdminApiEndpoints.updateProductCategory}/${id}`, data)
      return true
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getBroadcast'] })
      openSnackbar("Successfully updated broadcast", "success");
      fetchBroadcastMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to update broadcast", "error");
    }
  })

  const onSubmit = () => {
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    const data = getValues();
    // data.broadcastTypeGuid = data?.broadcastType.broadcastTypeGuid
    const formData = {
      ...data,
      broadcastTypeGuid: "25345b2c-c0e7-44a6-bf9b-7048141a30da",
      broadcastDate: dayjs().format("YYYY-MM-DD HH:mm:ss")
    }
    // console.log(formData)
    // return
    if(pageAction === "create")
      createBroadcastMutation.mutate(formData)
    else
      updateBroadcastkMutation.mutate(formData)
  }

  const onBack = () => {
    navigate('/admin/broadcasts', { replace: true });
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs />
      <SkeletonWrapper isLoading={fetchBroadcastMutation.isPending} isError={fetchBroadcastMutation.isError} errorMessage="Unable to load broadcast" disabled={["create"].includes(pageAction)}>
      <form onSubmit={handleSubmit(onSubmit)} style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Broadcast Details</Typography>
            </Grid2>
            {/* <Grid2 size={12}>
              <ControlledAutocomplete 
                name="broadcastType"
                control={control}
                label="Broadcast Type"
                disabled={fetchBroadcastMutation.isPending || fetchBroadcastMutation.isError || ["view"].includes(pageAction)}
                loading={broadcastTypeOptions.isFetching}
                options={broadcastTypeOptions.data}
                getOptionLabel={(option) => option.broadcastType}
                getOptionKey={(option) => option.broadcastTypeGuid}
                isOptionEqualToValue={(option, value) => option.broadcastTypeGuid === value?.broadcastTypeGuid}
              />
            </Grid2> */}

            <Grid2 size={12}>
              <ControlledTextField 
                name="title"
                control={control}
                label="Title"
                disabled={fetchBroadcastMutation.isPending || fetchBroadcastMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            {/* <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledDatePicker 
                name="broadcastDate"
                control={control}
                label="Broadcast Date"
                disabled={fetchBroadcastMutation.isPending || fetchBroadcastMutation.isError || ["view"].includes(pageAction)}
              />
            </Grid2> */}

            {["view", "edit"].includes(pageAction) && <>

            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdOn"
                control={control}
                label="Created On"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdBy"
                control={control}
                label="Created By"
                disabled={true}
              />
            </Grid2>
            
            </>}

          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Broadcast Content</Typography>
            </Grid2>
            <Grid2 size={12}>
              <ControlledRichTextEditor 
                name="message"
                control={control}
                disabled={["view"].includes(pageAction)}
              />
            </Grid2>
          </Grid2>
        </Paper>

        {["create", "edit"].includes(pageAction) && 
          <Paper
            sx={{
              p: "20px 25px",
              boxShadow: "none",
              borderRadius: "10px",
              border: "1px solid #E5E5E5",
              display: "flex",
              justifyContent: 'flex-end',
              gap: "10px"
            }}
          >
            <CustomButton type="submit" disabled={fetchBroadcastMutation.isPending || fetchBroadcastMutation.isError}>Submit</CustomButton>
          </Paper>
        }
      </form>
      </SkeletonWrapper>
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription="Submit broadcast?"
        onSubmit={()=>onConfirmSubmit()}
        loading={createBroadcastMutation.isPending || updateBroadcastkMutation.isPending}
      />
    </div>
  );
}
